import { useMemo, type MouseEvent, type RefObject } from "react"
import { onActivateKey } from "@/lib/onActivate"
import { useMetricDetails } from "./metricDetailsContext"
import { METRIC_LABELS } from "./metricLabels"
import type { MetricId } from "./metricDetailsTypes"

export type MetricTriggerProps = ReturnType<typeof useMetricTrigger>

// Shared props for a HUD element that opens a metric's details state on click or
// Enter/Space. One source of truth for the role/tabIndex/aria/handlers every
// trigger shares; the aria-label tracks the details title so they stay in sync.
// Spread onto the element and add any element-specific className/style/handlers.
// The activated element (or `originRef`, when the visual box differs from the
// hit target) is passed to openMetric as the label flight's origin.
// While details are open they overlay the HUD, so triggers drop out of the tab
// order — a covered (or otherwise invisible) block must not be keyboard-openable.
export const useMetricTrigger = (id: MetricId, originRef?: RefObject<HTMLElement>) => {
  const { openId, openMetric } = useMetricDetails()
  const panelOpen = openId !== null
  return useMemo(
    () => ({
      role: "button",
      tabIndex: panelOpen ? -1 : 0,
      "aria-haspopup": "dialog",
      "aria-expanded": openId === id,
      "aria-label": `${METRIC_LABELS[id]} — sources and details`,
      // Feeds the /analytics "Hovered elements" panel via ui/hoverTracking.tsx.
      "data-track-hover": `hud-metric-details:${id}`,
      onClick: (e: MouseEvent<HTMLElement>) => openMetric(id, originRef?.current ?? e.currentTarget),
      onKeyDown: onActivateKey((e) => openMetric(id, originRef?.current ?? e.currentTarget)),
    }) as const,
    [id, openMetric, originRef, panelOpen, openId],
  )
}
